<template>
    <div class="container pull-up">
        <el-card :body-style="{padding: '0 15px'}" style="margin-bottom: 15px">
            <el-row :gutter="24" style="margin-bottom: 10px; padding-top: 10px">
                <el-col :span="12">
                    <el-form size="small" label-position="left" label-width="120px" :model="form" :rules="validationRules"
                             ref="adjustmentForm">
                        <el-form-item :label="$t('warehouse.label.adjustment_date')" prop="date">
                            <el-date-picker v-model="form.date" type="date" autofocus
                                            :placeholder="$t('text.pick_day')"
                                            value-format="yyyy-MM-dd"></el-date-picker>
                        </el-form-item>
                        <el-form-item :label="$t('warehouse.label.reason')" prop="reason">
                            <el-input v-model="form.reason"></el-input>
                        </el-form-item>
                    </el-form>
                </el-col>
            </el-row>
        </el-card>
        <div class="row">
            <div class="col-md-12">
                <div class="card m-b-30">
                    <div class="card-body">
                        <el-row type="flex" justify="space-between">
                            <el-col :span="5">
                                <button
                                    :disabled="loading"
                                    @click="fetchItems"
                                    :class="[
                                        'px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200',
                                        'border-2 focus:outline-none focus:ring-2 focus:ring-offset-2',
                                        loading
                                            ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                                            : 'bg-blue-50 text-blue-700 border-blue-500 hover:bg-blue-100 hover:border-blue-600 focus:ring-blue-500'
                                    ]"
                                >
                                    <span class="flex items-center gap-2">
                                        {{ $t('warehouse.adjustment.load_all') }}
                                        <i v-if="loading" class="el-icon-loading animate-spin"></i>
                                        <i v-else class="el-icon-refresh"></i>
                                    </span>
                                </button>
                                <button
                                    v-if="warehouse.items"
                                    @click="clearData"
                                    class="ml-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 border-2 bg-cyan-50 text-cyan-700 border-cyan-500 hover:bg-cyan-100 hover:border-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                                >
                                    {{ $t('warehouse.adjustment.clear_all') }}
                                </button>
                            </el-col>
                            <el-col :span="10">
                                <el-input size="small" :placeholder="$t('text.search_placeholder')"
                                          v-model="filter.search" clearable>
                                            <span slot="append">
                                                <span class="el-icon-search"/>
                                            </span>
                                </el-input>
                            </el-col>
                        </el-row>
                        <el-table :data="filteredData" center-align max-height="500" :layout="buildLayout" class="adjust-wh-tb">

                            <el-table-column prop="code" :label="$t('warehouse.label.code')" width="100"
                                             show-overflow-tooltip fixed></el-table-column>
                            <el-table-column prop="name" :label="$t('label.name')" fixed></el-table-column>
                            <el-table-column prop="category.name"
                                             :label="$t('item.label.category')"></el-table-column>
                            <el-table-column align="right" show-overflow-tooltip prop="cost_price"
                                             :label="$t('warehouse.label.selling_price')">
                                <template v-slot:default="scope">
                                    {{ scope.row.pivot.selling_price | currency(' XAF') }}
                                </template>
                            </el-table-column>
                            <el-table-column align="right" show-overflow-tooltip prop="cost_price"
                                             :label="$t('warehouse.label.purchase')">
                                <template v-slot:default="scope">
                                    {{ scope.row.purchase_price | currency(' XAF') }}
                                </template>
                            </el-table-column>
                            <el-table-column align="center" prop="pivot.quantity"
                                             :label="$t('warehouse.label.qty_on_hand')">
                                <template v-slot:default="scope">
                                    {{ scope.row.pivot.quantity | currency }}
                                </template>
                            </el-table-column>
                            <el-table-column align="center" prop="new_qty"
                                             :label="$t('warehouse.label.new_qty_on_hand')">
                                <template v-slot="scope">
                                    <el-input-number size="mini" v-model="scope.row.new_qty" :min="0">
                                    </el-input-number>
                                </template>
                            </el-table-column>
                        </el-table>
                        <el-row class="justify-end mt-3">
                            <el-col align="end">
                                <el-button type="primary" @click="saveChanges" :disabled="!contentModified">
                                    {{ $t('warehouse.adjustment.save_changes') }}
                                </el-button>
                                <el-button type="primary" plain @click="$router.go(-1)">{{ $t('button.cancel') }}</el-button>
                            </el-col>
                        </el-row>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
  name: 'AdjustWarehouseStock',
  components: {},
  data () {
    return {
      form: {
        reason: '',
        date: null
      },
      loading: false,
      warehouse: {},
      filter: {
        search: ''
      }
    }
  },
  computed: {
    validationRules () {
      return {
        date: [
          {
            required: true,
            message: this.$t('warehouse.adjustment.date_required'),
            trigger: 'change'
          }
        ],
        reason: [
          {
            required: true,
            message: this.$t('warehouse.adjustment.reason_required'),
            trigger: 'blur'
          }
        ]
      }
    },
    filteredData () {
      return this.warehouse.items
        ? this.warehouse.items.filter(
          item => !this.filter.search || item.name.toLowerCase().includes(this.filter.search.toLowerCase()) ||
            item.code.toLowerCase().includes(this.filter.search.toLowerCase()))
        : []
    },
    buildLayout () {
      return this.warehouse.items ? 'tool, pagination, table, pagination' : 'tool, table'
    },
    contentModified () {
      return this.warehouse.items && this.warehouse.items.some(item => item.pivot.quantity !== item.new_qty)
    }
  },
  methods: {
    fetchItems () {
      this.anyChanges(() => {
        this.loading = true
        const whId = this.$route.params.id
        this.$warehouseService.fetchWarehouseItems(whId).then(data => {
          data.items = data.items.map(item => {
            // add new_qty field
            item.new_qty = item.pivot.quantity
            return item
          })
          this.warehouse = data
        }).then(() => (this.loading = false))
      })
    },
    async anyChanges (cb = () => {
    }) {
      if (this.contentModified) {
        this.askQuestion(this.$t('warehouse.adjustment.discard_changes'), cb, this.$t('warehouse.adjustment.unsaved_changes'))
      } else {
        cb()
      }
    },
    clearData () {
      this.anyChanges(() => (this.warehouse = {}))
    },
    saveChanges () {
      this.$refs.adjustmentForm.validate((valid) => {
        if (valid) {
          const items = this.warehouse.items.filter(item => item.pivot.quantity !== item.new_qty)
            .map((item, index, items) => {
              if (!index) {
                return `<ol><li>${item.name}: <code>${item.pivot.quantity} &longrightarrow; ${item.new_qty}</code></li>`
              } else if (index === items.length - 1) {
                return `<li>${item.name}: <code>${item.pivot.quantity} &longrightarrow; ${item.new_qty}</code></li></ol>`
              } else {
                return `<li>${item.name}: <code>${item.pivot.quantity} &longrightarrow; ${item.new_qty}</code></li>`
              }
            })
          this.askQuestion(items.join(''), () => {
            this.$warehouseService.adjustWarehouseStock(this.warehouse.id, { ...this.form, items: this.warehouse.items })
              .then(data => {
                console.log(data)
                this.displayMessage(this.$t('warehouse.adjustment.changes_saved'))
              })
          }, this.$t('warehouse.adjustment.save_adjustment'))
        } else {
        }
      })
    }
  },
  async mounted () {
    this.warehouse = await this.$warehouseService.getWarehouse(this.$route.params.id)
    this.$emit('parent-title', this.$t('warehouse.adjustment.adjust_stock_in', { name: this.warehouse.name }))
  },
  created () {
  }
}
</script>

<style>
.adjust-wh-tb .el-table.el-table--fit {
    max-height: 45vh;
    overflow-y: auto;
}
</style>
